import React, { useState, useEffect, useContext } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button, Space, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import { SpinnerContext } from "../../context/spinner/spinner";
import { ToastContext } from "../../context/toast/incredable-toast";
import { Attribute, SectionRequest } from "../../redux/api/section/types";
import StepOne from "./stepone";
import StepTwo from "./steptwo";
import StepThree from "./stepthree";
import StepFour from "./stepfour";
import { useAddSectionMutation } from "../../redux/api/section/section";
import IncredableContent from "../../components/incredable-content";

const AddSectionV2: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [section, setSection] = useState<SectionRequest | undefined>();
  const [sectionType, setSectionType] = useState("Provider");
  const [sectionAttribute, setSectionAttribute] = useState<Partial<Attribute>>({
    name: "",
    description: "",
    type: "",
    defaultValue: "",
    required: false,
    sensitive: false,
    optionDefinition: [],
  });
  const navigate = useNavigate();
  const [addSection] = useAddSectionMutation();
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);

  const methods = useForm({
    mode: "onBlur",
    defaultValues: section,
  });
  const { trigger, getValues } = methods;

  const name = methods.watch("name");
  const description = methods.watch("description");

  useEffect(() => {
    const sectionData: SectionRequest = {
      name: name,
      description: description,
      type: "",
      sortOrder: 0,
      isFacility: sectionType === "Provider" ? false : true,
      allowMultiple: false,
      attributes: section?.attributes || [],
      active: true
    };
    setSection(sectionData);
  }, [name, description, section, sectionType]);

  const steps = [
    {
      title: "Choose Section Type",
      description:
        "Specifies whether the section is for Provider or Facility-related data. Used to organize flex fields under the appropriate entity.",
      content: (
        <StepOne sectionType={sectionType} setSectionType={setSectionType} />
      ),
    },
    {
      title: "Section Details",
      description: "",
      content: <StepTwo />,
    },
    {
      title: `Add FlexFields to ${section?.name}`,
      description: "",
      content: (
        <StepThree
          section={section}
          setSection={setSection}
          sectionAttribute={sectionAttribute}
          setSectionAttribute={setSectionAttribute}
        />
      ),
    },
    {
      title: "Review and Save",
      description: "",
      content: <StepFour section={section} setCurrent={setCurrent} />,
    },
  ];

  const next = async () => {
    const valid = await trigger();
    if (!valid) {
      message.error("Please fix the errors before proceeding.");
      return;
    }
    setSection((prev: SectionRequest | undefined) => ({
      ...(prev ?? {}),
      ...getValues(),
    }));
    setCurrent((prev: number) => prev + 1);
  };

  const prev = () => {
    setCurrent((prev: number) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      if (section) {
        spinnerContext?.showSpinner();
        await addSection(section).unwrap();
        spinnerContext?.hidespinner();
        toastContext?.openSuccessNotification({
          message: `Section Saved successfully`,
          placement: "topRight",
        });
        navigate(-1);
      }
    } catch (error) {
      spinnerContext?.hidespinner();
      toastContext?.openWarningNotification({
        message: `Failed to save section`,
        placement: "topRight",
      });
    }
  };

  return (
    <IncredableContent
      style={{
        paddingBottom: "24px",
        paddingTop: "24px",
        margin: "20px 30px 0px",
      }}
      breadcrumbContainerStyle={{ margin: "20px 30px 0px" }}
      breadcrumbGroupProps={{
        items: [
          {
            text: "Dynamic Profiles",
            href: "/profilebuilder",
          },
          {
            text: "Add",
            href: "#",
          },
        ],
      }}
    >
      <FormProvider {...methods}>
        <Typography.Text strong>Step {current + 1} of 4:</Typography.Text>
        <Typography.Title level={4}>{steps[current].title}</Typography.Title>
        <Typography.Text>{steps[current].description}</Typography.Text>
        {steps[current].content}
        <div
          style={{
            marginTop: "25px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Space>
            <Button
              style={{ border: 0, color: "#598BEB", fontSize: "16px" }}
              onClick={() => navigate("/profilebuilder")}
              type="default"
            >
              Cancel
            </Button>
            {current > 0 && (
              <Button
                style={{
                  fontSize: "16px",
                  borderRadius: "15px",
                  color: "#598BEB",
                }}
                onClick={prev}
                type="default"
              >
                Previous
              </Button>
            )}
            {current < steps.length - 1 && (
              <Button
                style={{ fontSize: "16px", borderRadius: "15px" }}
                onClick={next}
                type="primary"
              >
                Next
              </Button>
            )}
            {current === steps.length - 1 && (
              <Button
                style={{ fontSize: "16px", borderRadius: "15px" }}
                onClick={handleSubmit}
                type="primary"
              >
                Save
              </Button>
            )}
          </Space>
        </div>
      </FormProvider>
    </IncredableContent>
  );
};

export default AddSectionV2;
